/**
 * dcent-pdp-social-reviews.js — DCENT S 소셜 리뷰 캐러셀 동작 (drop-04 R5).
 *
 * 디자인 SoT: drop-04-20260806/src/components/SocialReviews.tsx.
 * React state 로 돌던 동작을 프레임워크 없이 DOM 스타일·클래스 토글로 재현한다.
 *
 *   PC     — 화살표로 트랙을 transform 슬라이드(카드 1장 단위)
 *   모바일 — 네이티브 가로 스크롤(손 스와이프) + 화살표·세그먼트 인디케이터
 *
 * 수동 전용 캐러셀이라 양 끝에서 해당 방향 화살표를 비활성한다(가이드 §5 캐러셀 공통 규칙).
 *
 * 끝 판정은 인덱스가 아니라 **스크롤/이동 위치**로 한다 — 원본 React 가 목표를 i × step 으로만
 * 계산해서, 트랙이 그만큼 못 미는 폭(태블릿 등)에서 next 가 살아 있는 채로 먹통이 되는 문제가
 * 있었다. 여기서는 목표 이동량을 실제 최대치로 clamp 하고 "끝까지 밀렸는가"로 막는다.
 * ⚠ 인덱스 상한(floor(maxShift / step))으로 자르면 안 된다 — step 의 배수가 아닌 나머지 픽셀만큼
 * 마지막 카드가 영영 잘린 채로 남는다(dcent-app-page.js 에서 같은 회귀를 되돌린 적이 있다).
 *
 * 담기·옵션 상태는 여기서 다루지 않는다 — 그쪽은 dcent-pdp-buy.js 전담이다(drop-04 계약 §3).
 * 전역 상태 0 · 다중 호출 안전(초기화 완료 루트에 플래그). 테마 편집기 재로드 대응.
 */

const GAP = 16; // 카드 사이 간격(px) — CSS --dsr-gap 과 같은 값

/** 모션 최소화 선호 시 smooth 스크롤을 즉시 이동으로 낮춘다. */
function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/* ── PC: 버튼 캐러셀 ─────────────────────────────────────────────── */

function initPc(sec) {
  const viewport = sec.querySelector("[data-dcent-psr-pc-viewport]");
  const track = sec.querySelector("[data-dcent-psr-pc-track]");
  const prev = sec.querySelector("[data-dcent-psr-pc-prev]");
  const next = sec.querySelector("[data-dcent-psr-pc-next]");
  if (!viewport || !track || !prev || !next) return;

  const cards = track.querySelectorAll(".dcent-psr__card");
  if (cards.length === 0) return;
  const last = cards.length - 1;
  let idx = 0;

  const metrics = () => {
    const card = cards[0];
    const step = (card ? card.offsetWidth : 440) + GAP;
    const maxShift = Math.max(0, track.scrollWidth - viewport.clientWidth);
    return { step, maxShift };
  };

  const go = (i) => {
    const { step, maxShift } = metrics();
    idx = Math.max(0, Math.min(last, i));
    // 목표 이동량만 실제 최대치로 clamp 한다 — 인덱스는 자르지 않는다.
    const shift = Math.min(idx * step, maxShift);
    track.style.transform = "translateX(" + -shift + "px)";
    prev.disabled = idx === 0;
    // 트랙이 끝까지 밀린 뒤에만 next 를 막는다(그때 마지막 카드가 온전히 보인다).
    next.disabled = shift >= maxShift - 1 || idx === last;
  };

  prev.addEventListener("click", () => go(idx - 1));
  next.addEventListener("click", () => go(idx + 1));
  // 폭이 바뀌면 step·maxShift 가 달라지므로 현재 인덱스로 다시 계산
  window.addEventListener("resize", () => go(idx));
  go(0);
}

/* ── 모바일: 스와이프 + 화살표·인디케이터 ────────────────────────── */

function initMobile(sec) {
  const scroller = sec.querySelector("[data-dcent-psr-m-scroller]");
  const dots = sec.querySelectorAll("[data-dcent-psr-m-dot]");
  const prev = sec.querySelector("[data-dcent-psr-m-prev]");
  const next = sec.querySelector("[data-dcent-psr-m-next]");
  if (!scroller || !prev || !next) return;

  const cards = scroller.querySelectorAll(".dcent-psr__card");
  if (cards.length === 0) return;
  const last = cards.length - 1;
  let active = 0;

  const metrics = () => {
    const card = cards[0];
    const step = (card ? card.offsetWidth : 320) + GAP;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    return { step, maxScroll };
  };

  /**
   * 활성 인덱스·버튼 상태를 스크롤 위치로 확정한다.
   * assumedLeft — 클릭 직후 smooth 스크롤이 끝나기 전에 목표 위치로 먼저 반영할 때 쓴다.
   */
  const paint = (assumedLeft) => {
    const { step, maxScroll } = metrics();
    const left =
      typeof assumedLeft === "number" ? assumedLeft : scroller.scrollLeft;
    // 끝까지 밀린 상태면 마지막 카드가 온전히 보이므로 인디케이터도 마지막으로 맞춘다.
    active =
      maxScroll > 0 && left >= maxScroll - 1
        ? last
        : Math.max(0, Math.min(last, Math.round(left / step)));
    dots.forEach((d, i) => d.classList.toggle("is-active", i === active));
    prev.disabled = left <= 0;
    next.disabled = maxScroll <= 0 || left >= maxScroll - 1;
  };

  const scrollToIndex = (i) => {
    const { step, maxScroll } = metrics();
    const target = Math.max(0, Math.min(last, i));
    // 목표 위치만 clamp — 마지막 카드로 갈 때 남은 나머지 픽셀까지 끝까지 민다.
    const left = Math.min(target * step, maxScroll);
    scroller.scrollTo({
      left,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
    paint(left);
  };

  scroller.addEventListener("scroll", () => paint());
  prev.addEventListener("click", () => scrollToIndex(active - 1));
  next.addEventListener("click", () => scrollToIndex(active + 1));
  dots.forEach((d, i) => d.addEventListener("click", () => scrollToIndex(i)));
  window.addEventListener("resize", () => paint());
  paint();
}

export function initSocialReviews(root = document) {
  root.querySelectorAll("[data-dcent-psr]").forEach((sec) => {
    if (sec.__dcentPsr) return;
    sec.__dcentPsr = true;
    initPc(sec);
    initMobile(sec);
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initSocialReviews());
  // 테마 편집기가 섹션 DOM 을 AJAX 교체할 때 재초기화(라이브 스토어는 미발생)
  document.addEventListener("shopify:section:load", (e) =>
    initSocialReviews(e.target),
  );
}
